import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail, Send, AlertCircle, Filter, FileText, Loader2, Save, Calendar, Plus, X } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/shared/PageHeader";
import StatCard from "@/components/shared/StatCard";
import { Button } from "@/components/ui/button";
import { listDrafts, updateDraft, sendDraft, createDraftFromCompany, createCustomDraft, deleteDraft } from "./email-intel/api";

// Same High/Medium/Low values as lead_engine's own priority column -
// denormalized onto the draft at creation time. Colors aren't reused from
// anywhere else (Lead Engine shows priority as plain text, no badge yet),
// so this sets the convention: High=red, Medium=amber, Low=dim.
const PRIORITY_STYLES = {
  High: "bg-red-500/10 text-red-300 border-red-500/25",
  Medium: "bg-amber-500/10 text-amber-300 border-amber-500/25",
  Low: "bg-white/[0.03] text-white/50 border-white/[0.08]",
};

function PriorityBadge({ priority }) {
  if (!priority) return null;
  return (
    <span className={`text-[10px] font-mono uppercase tracking-wider rounded-full border px-1.5 py-0.5 shrink-0 ${PRIORITY_STYLES[priority] || PRIORITY_STYLES.Low}`}>
      {priority}
    </span>
  );
}

export default function EmailIntel() {
  // Real drafts sent over from Lead Engine ("Send to Draft" on a company's
  // personalized email) - this is the entire inbox now, mock data removed.
  const [drafts, setDrafts] = useState([]);
  const [loadingDrafts, setLoadingDrafts] = useState(true);
  const [selected, setSelected] = useState(null);
  const [creatingCustom, setCreatingCustom] = useState(false);

  const fetchDrafts = async () => {
    setLoadingDrafts(true);
    try {
      const data = await listDrafts({ limit: 50 });
      const items = data.items || [];
      setDrafts(items);
      setSelected((prev) => prev ?? items[0] ?? null);
    } catch (err) {
      console.error("Failed to load email drafts", err);
    } finally {
      setLoadingDrafts(false);
    }
  };

  useEffect(() => {
    fetchDrafts();
  }, []);

  // "+ Custom email" (page header) - a Gmail-compose-like blank draft with
  // no Lead Engine company behind it at all (see api.js's createCustomDraft
  // and DraftDetailPanel's editable "To" field below). Added to the top of
  // the list and selected immediately, same pattern as "Send another mail".
  const handleCreateCustom = async () => {
    setCreatingCustom(true);
    try {
      const draft = await createCustomDraft();
      setDrafts((prev) => [draft, ...prev]);
      setSelected(draft);
      toast.success("New draft created", { description: "Fill in To, Subject and Body, then send." });
    } catch (err) {
      console.error("Failed to create custom draft", err);
      toast.error("Could not create draft", { description: err.response?.data?.detail || err.message });
    } finally {
      setCreatingCustom(false);
    }
  };

  // Inbox row's per-draft delete ("x" on hover) - optimistic removal from
  // the list, with the actual DELETE fired in the background; resynced
  // (full refetch) if that request fails so the list doesn't end up lying
  // about what's actually in the database.
  const [deletingId, setDeletingId] = useState(null);
  const handleDeleteDraft = async (e, draft) => {
    e.stopPropagation(); // don't also select this row
    if (!window.confirm(`Delete this draft${draft.company_name ? ` for ${draft.company_name}` : ""}? This cannot be undone.`)) return;

    setDeletingId(draft.id);
    const wasSelected = selected?.id === draft.id;
    try {
      await deleteDraft(draft.id);
      setDrafts((prev) => {
        const next = prev.filter((d) => d.id !== draft.id);
        if (wasSelected) setSelected(next[0] ?? null);
        return next;
      });
      toast.success("Draft deleted");
    } catch (err) {
      console.error("Failed to delete draft", err);
      toast.error("Could not delete draft", { description: err.response?.data?.detail || err.message });
    } finally {
      setDeletingId(null);
    }
  };

  const unanswered = drafts.filter((d) => d.status !== "sent").length;
  const highPriority = drafts.filter((d) => d.priority === "High").length;
  const sent = drafts.filter((d) => d.status === "sent").length;

  return (
    <div data-testid="email-page">
      <PageHeader
        eyebrow="Module 07 · Email Lead Intelligence"
        title="Your inbox, decoded."
        description="Every incoming email is monitored, intent-classified, priority-scored and turned into a CRM opportunity — with an AI-suggested reply pre-drafted."
        actions={
          <div className="flex items-center gap-2">
            <Button
              data-testid="email-custom-compose"
              variant="outline"
              disabled={creatingCustom}
              className="rounded-full bg-indigo-500 hover:bg-indigo-400"
              onClick={handleCreateCustom}
            >
              {creatingCustom ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
              {creatingCustom ? "Creating..." : "+ Custom email"}
            </Button>
            {/* <Button data-testid="email-connect-inbox" className="rounded-full bg-indigo-500 hover:bg-indigo-400">
              <Mail className="h-3.5 w-3.5 mr-1.5" /> Connect inbox
            </Button> */}
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard testId="email-stat-new"      label="Total"       value={drafts.length} accent="indigo" icon={Mail} />
        <StatCard testId="email-stat-priority" label="High Priority" value={highPriority} accent="pink" icon={AlertCircle} />
        <StatCard testId="email-stat-unread"   label="Draft"  value={unanswered} accent="amber" trend="up" />
        <StatCard testId="email-stat-conv"     label="Sent"        value={sent} accent="emerald" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Inbox list */}
        <div className="lg:col-span-2 glass-card p-2" data-testid="email-inbox-list">
          <div className="flex items-center justify-between px-3 py-2">
            <div className="font-mono text-[10px] uppercase tracking-wider text-white/50">
              Inbox · {drafts.length} message{drafts.length === 1 ? "" : "s"}{loadingDrafts ? " · loading..." : ""}
            </div>
            <Button variant="ghost" size="sm" className="text-white/50 h-7 px-2 rounded-full">
              <Filter className="h-3 w-3 mr-1" /> Filter
            </Button>
          </div>
          <div className="space-y-1">
            {loadingDrafts && drafts.length === 0 ? (
              <div className="flex items-center justify-center gap-2 px-3 py-10 text-sm text-white/50">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading...
              </div>
            ) : drafts.length === 0 ? (
              <div className="px-3 py-10 text-center text-sm text-white/50">
                No drafts yet — send one from a lead's Personalized Email in Lead Engine.
              </div>
            ) : (
              drafts.map((e, i) => {
                const isActive = selected?.id === e.id;
                return (
                  <motion.button
                    key={e.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    onClick={() => setSelected(e)}
                    data-testid={`email-draft-${e.id}`}
                    className={`group relative w-full text-left rounded-xl px-3 py-3 pr-9 transition-colors ${
                      isActive ? "bg-white/[0.05] border border-indigo-400/25" : "border border-transparent hover:bg-white/[0.02]"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[13px] truncate text-white">
                        {e.company_name || (!e.company_id ? "Custom Email" : "—")}
                      </span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <PriorityBadge priority={e.priority} />
                        <span className="text-[10px] font-mono uppercase tracking-wider rounded-full border px-1.5 py-0.5 bg-blue-500/10 text-blue-300 border-blue-500/25">
                          {e.status === "sent" ? "Sent" : "Draft"}
                        </span>
                      </div>
                    </div>
                    <div className="mt-0.5 text-[12.5px] truncate text-white/55">
                      {[e.first_name, e.last_name].filter(Boolean).join(" ") || e.email || e.company_url || "New custom email"}
                    </div>
                    <button
                      type="button"
                      data-testid={`email-draft-delete-${e.id}`}
                      onClick={(ev) => handleDeleteDraft(ev, e)}
                      disabled={deletingId === e.id}
                      title="Delete draft"
                      className="absolute right-2.5 top-2.5 rounded-full p-1 text-white/30 opacity-0 transition-opacity hover:bg-white/10 hover:text-rose-300 group-hover:opacity-100 disabled:opacity-100"
                    >
                      {deletingId === e.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                    </button>
                  </motion.button>
                );
              })
            )}
          </div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-3">
          {selected ? (
            <DraftDetailPanel
              key={selected.id}
              draft={selected}
              onChange={(updated) => {
                setSelected(updated);
                setDrafts((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
              }}
              onCreateAnother={(newDraft) => {
                // A genuinely new draft row (own id) - added to the top of
                // the list and selected, not merged into the existing one.
                setDrafts((prev) => [newDraft, ...prev]);
                setSelected(newDraft);
              }}
            />
          ) : (
            <div className="glass-card flex h-full min-h-[200px] items-center justify-center p-6 text-sm text-white/40">
              Select a draft to view and customize its email.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Customizable editor for a Lead Engine draft - shown in the same detail
// pane spot the mock AI-inbox detail view uses, but for drafts specifically:
// editable Subject/Body, "Send email" (sends whatever's currently in the
// form - saves first if it's been edited, so what's sent always matches
// what's stored), and "Save changes" (persists the edit without sending,
// overwriting the original draft per the ask).
function DraftDetailPanel({ draft, onChange, onCreateAnother }) {
  const [subject, setSubject] = useState(draft.subject || "");
  const [body, setBody] = useState(draft.body || "");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [creatingAnother, setCreatingAnother] = useState(false);
  // Per-email choice, not persisted with the draft (see api.js's sendDraft) -
  // resets to unchecked for every draft on purpose, so it's a deliberate
  // decision each time rather than something that could quietly "stick"
  // from a previous email.
  const [includeMeetingLink, setIncludeMeetingLink] = useState(false);
  // Only actually editable for a "+ Custom email" draft (isCustom below) -
  // a Lead Engine-sourced draft's recipient stays the read-only contact
  // email it was created with, same as before this field existed.
  const [toEmail, setToEmail] = useState(draft.email || "");

  useEffect(() => {
    setSubject(draft.subject || "");
    setBody(draft.body || "");
    setIncludeMeetingLink(false);
    setToEmail(draft.email || "");
  }, [draft.id]);

  const dirty =
    subject !== (draft.subject || "") ||
    body !== (draft.body || "") ||
    toEmail !== (draft.email || "");
  const isSent = draft.status === "sent";
  // No lead_engine company behind this draft at all - see
  // create_custom_draft()/EmailDraftORM.company_id's docstrings.
  const isCustom = !draft.company_id;

  const handleSave = async () => {
    setSaving(true);
    try {
      const updated = await updateDraft(draft.id, { subject, body, email: isCustom ? toEmail : undefined });
      onChange({ ...updated, draft: true });
      toast.success("Draft saved", { description: "Your changes replaced the original draft." });
    } catch (err) {
      console.error("Failed to save draft", err);
      toast.error("Could not save draft", { description: err.response?.data?.detail || err.message });
    } finally {
      setSaving(false);
    }
  };

  // "Send another mail" - only offered once this draft is already sent (see
  // the button below). Creates a second, blank-subject/body draft for the
  // same company/contact rather than reusing this one, so the sent draft
  // stays exactly as a record of what was actually sent.
  const handleSendAnother = async () => {
    setCreatingAnother(true);
    try {
      const newDraft = await createDraftFromCompany(draft.company_id, { empty: true });
      onCreateAnother(newDraft);
      toast.success("New draft created", { description: "Write your email below, then save or send it." });
    } catch (err) {
      console.error("Failed to create another draft", err);
      toast.error("Could not create a new draft", { description: err.response?.data?.detail || err.message });
    } finally {
      setCreatingAnother(false);
    }
  };

  const handleSend = async () => {
    if (isCustom && !toEmail.trim()) {
      toast.error("Recipient required", { description: "Enter a 'To' email address before sending." });
      return;
    }
    setSending(true);
    try {
      // Sends exactly what's in the form right now (edited or not) in one
      // request - the backend persists it as the draft's saved state too,
      // so there's no separate save-then-send race to worry about here.
      const sent = await sendDraft(draft.id, { subject, body, includeMeetingLink, email: isCustom ? toEmail : undefined });
      onChange({ ...sent, draft: true });
      toast.success("Email sent", { description: `To ${toEmail || draft.email || draft.company_name}` });
    } catch (err) {
      console.error("Failed to send draft", err);
      toast.error("Could not send email", { description: err.response?.data?.detail || err.message });
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      className="glass-card p-6"
      data-testid="email-draft-detail"
    >
      <div className="flex items-start justify-between gap-3 mb-5">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <span className="inline-flex items-center text-[10px] font-mono uppercase tracking-wider rounded-full border px-1.5 py-0.5 bg-blue-500/10 text-blue-300 border-blue-500/25">
              <FileText className="h-2.5 w-2.5 mr-1" />
              {isSent ? "Sent" : "Draft"}{!isCustom && " · from Lead Engine"}
            </span>
            <PriorityBadge priority={draft.priority} />
          </div>
          <div className="font-display text-2xl text-white leading-tight">{draft.company_name || (isCustom ? "Custom Email" : "—")}</div>
          {!isCustom && (
            <div className="mt-1 text-[13px] text-white/60">
              <span className="text-white/80">{[draft.first_name, draft.last_name].filter(Boolean).join(" ") || "No contact"}</span>
              {draft.email ? ` · ${draft.email}` : ""}{draft.company_url ? ` · ${draft.company_url}` : ""}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        {isCustom && (
          <div>
            <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-white/40">To</div>
            <input
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              disabled={isSent}
              placeholder="recipient@example.com"
              className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-2.5 text-sm text-white outline-none transition-all focus:border-indigo-500 disabled:opacity-60"
            />
          </div>
        )}
        <div>
          <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-white/40">Subject</div>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            disabled={isSent}
            className="w-full rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-2.5 text-sm text-white outline-none transition-all focus:border-indigo-500 disabled:opacity-60"
          />
        </div>
        <div>
          <div className="mb-1.5 font-mono text-[10px] uppercase tracking-wider text-white/40">Body</div>
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            disabled={isSent}
            rows={12}
            className="w-full whitespace-pre-wrap rounded-xl border border-white/[0.08] bg-white/[0.02] px-4 py-3 text-sm leading-relaxed text-white/85 outline-none transition-all focus:border-indigo-500 disabled:opacity-60"
          />
        </div>
        <label className="flex items-center gap-2 text-[13px] text-white/70 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={includeMeetingLink}
            onChange={(e) => setIncludeMeetingLink(e.target.checked)}
            disabled={isSent}
            className="h-4 w-4 rounded border-white/20 bg-white/[0.02] accent-indigo-500 disabled:opacity-60"
          />
          <Calendar className="h-3.5 w-3.5 text-white/40" />
          Include meeting scheduling link
          <span className="text-white/35">— adds a "Schedule Meeting" button to the email (not shown in this preview)</span>
        </label>
      </div>

      <div className="mt-5 flex items-center gap-2">
        <Button
          data-testid="email-draft-send"
          disabled={isSent || sending || saving}
          className="rounded-full bg-indigo-500 hover:bg-indigo-400"
          onClick={handleSend}
        >
          {sending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1.5" />}
          {isSent ? "Already sent" : sending ? "Sending..." : "Send email"}
        </Button>
        {isSent && (
          <Button
            data-testid="email-draft-send-another"
            variant="ghost"
            disabled={creatingAnother}
            className="rounded-full text-white/70 border border-white/[0.06]"
            onClick={handleSendAnother}
          >
            {creatingAnother ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-1.5" />}
            {creatingAnother ? "Creating..." : "Send another mail"}
          </Button>
        )}
        <Button
          variant="ghost"
          disabled={isSent || !dirty || saving || sending}
          className="rounded-full text-white/70 border border-white/[0.06]"
          onClick={handleSave}
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
          {saving ? "Saving..." : "Save changes"}
        </Button>
        {dirty && !isSent && <span className="font-mono text-[11px] text-amber-300/80">Unsaved changes</span>}
      </div>
    </motion.div>
  );
}
